import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { cn } from "../lib/utils";
import MyTimeOffCard from "../components/MyTimeOffCard";
import {
  User,
  Mail,
  Phone,
  MessageCircle,
  Briefcase,
  Calendar,
  Edit2,
  Save,
  AlertCircle,
  Shield,
  Building2,
  MapPin,
  CreditCard,
  FileText,
  AlertTriangle,
  Lock,
  Eye,
  Bell,
  EyeOff,
  ChevronRight,
  Upload,
} from "lucide-react";

const ProfilePage = () => {
  const { user, refreshUser } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [editContactOpen, setEditContactOpen] = useState(false);
  const [contactForm, setContactForm] = useState({ phone: "", telegram: "" });
  const [saving, setSaving] = useState(false);
  const [ratesForm, setRatesForm] = useState({});
  const [hoursForm, setHoursForm] = useState({});
  const [maxWeeklyHours, setMaxWeeklyHours] = useState(40);
  const [savingRates, setSavingRates] = useState(false);
  const [taskTypes, setTaskTypes] = useState([]);
  const [editingRates, setEditingRates] = useState(false);
  
  // Email notifications state
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [savingNotifications, setSavingNotifications] = useState(false);
  
  // Profile edit state
  const [editProfileOpen, setEditProfileOpen] = useState(false);
  const [profileForm, setProfileForm] = useState({ name: "", avatar_url: "" });
  const [savingProfile, setSavingProfile] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const avatarInputRef = useRef(null);
  
  // Password change state
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current: "", new: "", confirm: "" });
  const [savingPassword, setSavingPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  
  // Email edit state
  const [editEmailOpen, setEditEmailOpen] = useState(false);
  const [emailForm, setEmailForm] = useState("");
  const [savingEmail, setSavingEmail] = useState(false);
  
  // Payment methods edit state
  const [editPaymentOpen, setEditPaymentOpen] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    preferred_method: "card",
    card: { card_number: "", bank: "", recipient_name: "", recipient_iban: "", recipient_ipn: "" },
    fop: { iban: "", company_name: "", bank_name: "", edrpou: "" },
    crypto: { wallet_address: "", network: "TRC20" }
  });
  const [savingPayment, setSavingPayment] = useState(false);

  useEffect(() => {
    fetchProfile();
    fetchTaskTypes();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const response = await api.get("/profile/me");
      setProfile(response.data);
      if (response.data.employee) {
        setContactForm({
          phone: response.data.employee.phone || "",
          telegram: response.data.employee.telegram || "",
        });
      }
      // Load desired rates for artists
      if (response.data.user?.desired_rates) {
        setRatesForm(response.data.user.desired_rates);
      }
      // Load hours per task type for artists
      if (response.data.user?.hours_per_task_type) {
        setHoursForm(response.data.user.hours_per_task_type);
      }
      if (response.data.user?.max_weekly_hours) {
        setMaxWeeklyHours(response.data.user.max_weekly_hours);
      }
      // Load email notification preference
      setEmailNotifications(response.data.user?.email_notifications !== false);
      // Load email for edit form
      setEmailForm(response.data.user?.email || "");
      // Load payment methods if employee
      if (response.data.employee?.payment_methods) {
        const pm = response.data.employee.payment_methods;
        setPaymentForm({
          preferred_method: pm.preferred_method || "card",
          card: pm.card || { card_number: "", bank: "", recipient_name: "", recipient_iban: "", recipient_ipn: "" },
          fop: pm.fop || { iban: "", company_name: "", bank_name: "", edrpou: "" },
          crypto: pm.crypto || { wallet_address: "", network: "TRC20" }
        });
      }
    } catch (error) {
      console.error("Error fetching profile:", error);
      toast.error("Failed to load profile");
    } finally {
      setLoading(false);
    }
  };
  
  const handleToggleEmailNotifications = async (checked) => {
    try {
      setSavingNotifications(true);
      await api.put(`/users/${profile.user.id}`, { email_notifications: checked });
      setEmailNotifications(checked);
      toast.success(checked ? "Email notifications enabled" : "Email notifications disabled");
    } catch (error) {
      toast.error("Failed to update notification settings");
    } finally {
      setSavingNotifications(false);
    }
  };
  
  const handleSaveEmail = async () => {
    if (!emailForm || !emailForm.includes("@")) {
      toast.error("Please enter a valid email address");
      return;
    }
    try {
      setSavingEmail(true);
      await api.put(`/users/${profile.user.id}`, { email: emailForm });
      setProfile(prev => ({ ...prev, user: { ...prev.user, email: emailForm } }));
      setEditEmailOpen(false);
      toast.success("Email updated successfully");
      refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update email");
    } finally {
      setSavingEmail(false);
    }
  };
  
  const handleSavePaymentMethods = async () => {
    try {
      setSavingPayment(true);
      // Use dedicated endpoint that allows self-update
      await api.put(`/profile/payment-methods`, { payment_methods: paymentForm });
      setProfile(prev => ({ 
        ...prev, 
        employee: { ...prev.employee, payment_methods: paymentForm } 
      }));
      setEditPaymentOpen(false);
      toast.success("Payment methods updated successfully");
    } catch (error) {
      toast.error("Failed to update payment methods");
    } finally {
      setSavingPayment(false);
    }
  };

  const fetchTaskTypes = async () => {
    try {
      const res = await api.get("/service-types/list");
      if (res.data && res.data.length > 0) {
        const types = res.data.map(s => ({
          value: s.name,
          label: s.name
        }));
        setTaskTypes(types);
      }
    } catch (error) {
      console.error("Error fetching task types:", error);
      // Fallback to defaults
      setTaskTypes([
        { value: "exterior", label: "Exterior" },
        { value: "interior", label: "Interior" },
        { value: "aerial", label: "Aerial" },
        { value: "animation", label: "Animation" },
        { value: "other", label: "Other" },
      ]);
    }
  };

  const handleSaveContact = async () => {
    try {
      setSaving(true);
      await api.put("/profile/contact", contactForm);
      toast.success("Contact info updated");
      setEditContactOpen(false);
      fetchProfile();
    } catch (error) {
      toast.error("Failed to update contact info");
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Only image files are allowed");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image is too large (max 5 MB)");
      return;
    }
    try {
      setUploadingAvatar(true);
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/profile/me/avatar", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = res.data?.avatar_url;
      setProfileForm((p) => ({ ...p, avatar_url: url }));
      toast.success("Photo uploaded");
      await fetchProfile();
      if (refreshUser) await refreshUser();
    } catch (error) {
      toast.error("Failed to upload photo");
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleSaveProfile = async () => {
    try {
      setSavingProfile(true);
      const updates = {};
      if (profileForm.name && profileForm.name !== profile.user.name) {
        updates.name = profileForm.name;
      }
      if (profileForm.avatar_url !== profile.user.avatar_url) {
        updates.avatar_url = profileForm.avatar_url || null;
      }
      
      if (Object.keys(updates).length > 0) {
        await api.put(`/users/${profile.user.id}`, updates);
        toast.success("Profile updated");
        await fetchProfile();
        // Refresh auth context to update header
        if (refreshUser) {
          await refreshUser();
        }
      }
      setEditProfileOpen(false);
    } catch (error) {
      toast.error("Failed to update profile");
    } finally {
      setSavingProfile(false);
    }
  };

  const handleSaveRates = async () => {
    try {
      setSavingRates(true);
      // Save rates
      await api.put("/artist/my-rates", ratesForm);
      // Save hours and max weekly hours
      await api.put("/artist/load-settings", {
        hours_per_task_type: hoursForm,
        max_weekly_hours: maxWeeklyHours,
      });
      toast.success("Settings saved");
      setEditingRates(false);
      fetchProfile();
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSavingRates(false);
    }
  };

  const handleHoursChange = (taskType, value) => {
    const numValue = parseInt(value) || 0;
    setHoursForm(prev => ({ ...prev, [taskType]: numValue }));
  };

  const handleChangePassword = async () => {
    // Validation
    if (!passwordForm.current) {
      toast.error("Please enter your current password");
      return;
    }
    if (!passwordForm.new) {
      toast.error("Please enter a new password");
      return;
    }
    if (passwordForm.new.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    if (passwordForm.new !== passwordForm.confirm) {
      toast.error("New passwords do not match");
      return;
    }
    
    try {
      setSavingPassword(true);
      await api.post("/auth/change-password", {
        current_password: passwordForm.current,
        new_password: passwordForm.new,
      });
      toast.success("Password changed successfully");
      setPasswordDialogOpen(false);
      setPasswordForm({ current: "", new: "", confirm: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change password");
    } finally {
      setSavingPassword(false);
    }
  };

  const handleRateChange = (type, value) => {
    const numValue = parseInt(value) || 0;
    if (numValue === 0) {
      const { [type]: _, ...rest } = ratesForm;
      setRatesForm(rest);
    } else {
      setRatesForm({ ...ratesForm, [type]: numValue });
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case "admin": return "bg-red-500/10 text-red-400 border-red-500/20";
      case "team": return "bg-sky-500/10 text-sky-400 border-sky-500/20";
      case "artist": return "bg-purple-500/10 text-purple-400 border-purple-500/20";
      case "client": return "bg-green-500/10 text-green-400 border-green-500/20";
      case "sales": return "bg-orange-500/10 text-orange-400 border-orange-500/20";
      default: return "bg-zinc-800 text-zinc-400";
    }
  };

  const getPositionLabel = (position) => {
    const labels = {
      project_manager: "Project Manager",
      art_director: "Art Director",
      "3d_artist": "3D Artist",
      animator: "Animator",
      designer: "Designer",
      developer: "Developer",
      admin: "Administrator",
      accountant: "Accountant",
      hr: "HR Manager",
      marketing: "Marketing",
      sales: "Sales Manager",
      support: "Support",
      other: "Other"
    };
    return labels[position] || position;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-orange-500" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <AlertCircle className="h-12 w-12 text-red-500" />
        <p className="text-zinc-400">Failed to load profile</p>
      </div>
    );
  }

  const employee = profile.employee;

  return (
    <div data-testid="profile-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-zinc-50 flex items-center gap-3">
            <User className="h-7 w-7 text-sky-400" />
            My Profile
          </h1>
          <p className="text-sm text-zinc-500 mt-1">Personal and contact information</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Account Info */}
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-zinc-50 flex items-center gap-2">
              <Shield className="h-5 w-5 text-sky-400" />
              Account
            </CardTitle>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setPasswordDialogOpen(true)}
              className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
              data-testid="change-password-btn"
            >
              <Lock className="h-4 w-4 mr-2" />
              Change Password
            </Button>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Avatar and name */}
            <div className="flex items-center gap-4 pb-4 border-b border-zinc-800">
              <div className="h-16 w-16 rounded-full bg-gradient-to-br from-orange-500 to-amber-600 flex items-center justify-center overflow-hidden">
                {profile.user.avatar_url ? (
                  <img src={profile.user.avatar_url} alt={profile.user.name} className="h-full w-full object-cover" />
                ) : (
                  <span className="text-2xl font-bold text-zinc-50">{profile.user.name?.charAt(0).toUpperCase()}</span>
                )}
              </div>
              <div className="flex-1">
                <p className="text-lg font-semibold text-zinc-50">{profile.user.name}</p>
                <Badge variant="outline" className={cn("mt-1 capitalize", getRoleBadgeColor(profile.user.role))}>
                  {profile.user.role}
                </Badge>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setProfileForm({ 
                    name: profile.user.name || "", 
                    avatar_url: profile.user.avatar_url || "" 
                  });
                  setEditProfileOpen(true);
                }}
                className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                data-testid="edit-profile-btn"
              >
                <Edit2 className="h-4 w-4" />
              </Button>
            </div>
            
            {/* Details */}
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <Mail className="h-4 w-4 text-zinc-500" />
                <span className="text-sm text-zinc-400">Email</span>
                <span className="text-sm text-zinc-50 ml-auto font-mono">{profile.user.email}</span>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setEditEmailOpen(true)}
                  className="h-7 w-7 p-0 text-zinc-500 hover:text-orange-400"
                >
                  <Edit2 className="h-3.5 w-3.5" />
                </Button>
              </div>
              
              {employee && (
                <>
                  <div className="flex items-center gap-3">
                    <Briefcase className="h-4 w-4 text-zinc-500" />
                    <span className="text-sm text-zinc-400">Position</span>
                    <span className="text-sm text-zinc-50 ml-auto">{getPositionLabel(employee.position)}</span>
                  </div>
                  
                  {employee.hire_date && (
                    <div className="flex items-center gap-3">
                      <Calendar className="h-4 w-4 text-zinc-500" />
                      <span className="text-sm text-zinc-400">Hire Date</span>
                      <span className="text-sm text-zinc-50 ml-auto">{employee.hire_date}</span>
                    </div>
                  )}
                  
                  <div className="flex items-center gap-3">
                    <Building2 className="h-4 w-4 text-zinc-500" />
                    <span className="text-sm text-zinc-400">Status</span>
                    <Badge className={cn("ml-auto", employee.is_active ? "bg-green-900/30 text-green-400" : "bg-red-900/30 text-red-400")}>
                      {employee.is_active ? "Active" : "Inactive"}
                    </Badge>
                  </div>
                </>
              )}
              
              {profile.user.created_at && (
                <div className="flex items-center gap-3">
                  <Calendar className="h-4 w-4 text-zinc-500" />
                  <span className="text-sm text-zinc-400">Account Created</span>
                  <span className="text-sm text-zinc-50 ml-auto">{profile.user.created_at.split("T")[0]}</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Contact Info */}
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-zinc-50 flex items-center gap-2">
              <MessageCircle className="h-5 w-5 text-green-400" />
              Contact Information
            </CardTitle>
            {employee && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setEditContactOpen(true)}
                className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                data-testid="edit-contact-btn"
              >
                <Edit2 className="h-4 w-4 mr-2" />
                Edit
              </Button>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            {employee ? (
              <div className="space-y-4">
                <div className="p-4 bg-zinc-800/50 rounded-lg">
                  <div className="flex items-center gap-2 text-xs text-zinc-500 mb-2">
                    <Phone className="h-3 w-3" />
                    Phone Number
                  </div>
                  <p className="text-zinc-50 font-mono text-lg">{employee.phone || "Not set"}</p>
                </div>
                
                <div className="p-4 bg-zinc-800/50 rounded-lg">
                  <div className="flex items-center gap-2 text-xs text-zinc-500 mb-2">
                    <MessageCircle className="h-3 w-3" />
                    Telegram
                  </div>
                  <p className="text-zinc-50 font-mono text-lg">{employee.telegram || "Not set"}</p>
                </div>
              </div>
            ) : (
              <div className="p-6 bg-zinc-800/30 rounded-lg text-center">
                <User className="h-10 w-10 text-zinc-600 mx-auto mb-3" />
                <p className="text-zinc-400">No employee profile linked</p>
                <p className="text-zinc-600 text-sm mt-1">Contact admin to set up your profile</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Notification Settings */}
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-zinc-50 flex items-center gap-2">
              <Bell className="h-5 w-5 text-amber-400" />
              Notification Settings
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-zinc-800/50 rounded-lg">
              <div className="flex items-center gap-3">
                <Mail className="h-5 w-5 text-zinc-400" />
                <div>
                  <p className="text-zinc-50 font-medium">Email Notifications</p>
                  <p className="text-xs text-zinc-500">Receive email alerts for tasks, reviews, and updates</p>
                </div>
              </div>
              <Switch
                checked={emailNotifications}
                onCheckedChange={handleToggleEmailNotifications}
                disabled={savingNotifications}
                data-testid="email-notifications-switch"
              />
            </div>
            
            {/* Telegram Settings Link */}
            <Link 
              to="/settings/notifications"
              className="flex items-center justify-between p-4 bg-blue-900/20 border border-blue-800/30 rounded-lg hover:bg-blue-900/30 transition-colors"
              data-testid="telegram-settings-link"
            >
              <div className="flex items-center gap-3">
                <MessageCircle className="h-5 w-5 text-blue-400" />
                <div>
                  <p className="text-zinc-50 font-medium">Telegram Notifications</p>
                  <p className="text-xs text-zinc-500">Configure Telegram bot for real-time alerts</p>
                </div>
              </div>
              <ChevronRight className="h-5 w-5 text-blue-400" />
            </Link>
            
            <p className="text-xs text-zinc-600 px-2">
              When enabled, you will receive notifications for: new task assignments, work ready for review,
              revision requests, and approaching deadlines.
            </p>
          </CardContent>
        </Card>

        {/* Time Off (vacation + sick leave requests). Only on-payroll staff —
            artists are project contractors, they don't accrue PTO. Matches the
            /team-calendar filter so a submitted request would actually be seen
            by managers. */}
        {["admin", "team", "pm", "pm_lead", "sales", "sales_director",
          "crm_manager", "art_director", "finance"].includes(profile.user.role) && <MyTimeOffCard />}

        {/* Payment Methods (for employees - show even if empty to allow adding) */}
        {employee && (
          <Card className="bg-zinc-900/50 border-zinc-800 lg:col-span-2">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-zinc-50 flex items-center gap-2">
                <CreditCard className="h-5 w-5 text-purple-400" />
                Payment Methods
              </CardTitle>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setEditPaymentOpen(true)}
                className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
              >
                <Edit2 className="h-4 w-4 mr-1" />
                {employee?.payment_methods && Object.keys(employee.payment_methods).length > 0 ? "Edit" : "Add"}
              </Button>
            </CardHeader>
            <CardContent>
              {employee?.payment_methods && typeof employee.payment_methods === 'object' && Object.keys(employee.payment_methods).length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {Object.entries(employee.payment_methods || {}).map(([method, details]) => {
                    if (method === "preferred_method") return null;
                    return (
                      <div key={method} className="p-4 bg-zinc-800/50 rounded-lg">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge className={cn(
                            "text-xs capitalize",
                            employee.payment_methods.preferred_method === method
                              ? "bg-green-900/30 text-green-400 border-green-700"
                              : "bg-purple-900/30 text-purple-400 border-purple-700"
                          )}>
                            {method === "fop" ? "ФОП" : method === "card" ? "Карта" : method === "crypto" ? "Крипто" : method}
                            {employee.payment_methods.preferred_method === method && " ✓"}
                          </Badge>
                        </div>
                        {details && typeof details === 'object' && !Array.isArray(details) ? (
                          <div className="space-y-1">
                            {Object.entries(details).map(([key, value]) => value && (
                              <p key={key} className="text-xs text-zinc-400">
                                <span className="capitalize">{key.replace(/_/g, ' ')}:</span> <span className="text-zinc-50 font-mono">{value}</span>
                              </p>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-zinc-50 font-mono">{details}</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-zinc-500">
                  <CreditCard className="h-10 w-10 mx-auto mb-3 opacity-50" />
                  <p>Реквізити не заповнені</p>
                  <p className="text-xs mt-1">Натисніть "Add" щоб додати реквізити для оплати</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Desired Rates & Hours for Artists - Combined Table */}
        {profile.user.role === "artist" && (
          <Card className="bg-zinc-900/50 border-zinc-800 lg:col-span-2" data-testid="rates-card">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-zinc-50 flex items-center gap-2 text-base">
                <CreditCard className="h-4 w-4 text-yellow-400" />
                My Rates & Workload
              </CardTitle>
              {!editingRates ? (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setRatesForm(profile.user.desired_rates || {});
                    setHoursForm(profile.user.hours_per_task_type || {});
                    setMaxWeeklyHours(profile.user.max_weekly_hours || 40);
                    setEditingRates(true);
                  }}
                  className="h-7 text-xs text-zinc-400 hover:text-zinc-50"
                >
                  <Edit2 className="h-3 w-3 mr-1" />
                  Edit
                </Button>
              ) : (
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setRatesForm(profile.user.desired_rates || {});
                      setHoursForm(profile.user.hours_per_task_type || {});
                      setMaxWeeklyHours(profile.user.max_weekly_hours || 40);
                      setEditingRates(false);
                    }}
                    className="h-7 text-xs text-zinc-400"
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleSaveRates}
                    disabled={savingRates}
                    className="h-7 text-xs bg-yellow-600 hover:bg-yellow-700"
                  >
                    <Save className="h-3 w-3 mr-1" />
                    {savingRates ? "..." : "Save"}
                  </Button>
                </div>
              )}
            </CardHeader>
            <CardContent>
              {/* Max Weekly Hours */}
              <div className="flex items-center justify-between p-3 mb-4 rounded-lg bg-zinc-800/50 border border-zinc-700">
                <span className="text-sm text-zinc-400">Max Weekly Hours</span>
                {editingRates ? (
                  <Input
                    type="number"
                    value={maxWeeklyHours}
                    onChange={(e) => setMaxWeeklyHours(parseInt(e.target.value) || 40)}
                    className="w-20 h-8 bg-zinc-900 border-zinc-700 text-zinc-50 text-center"
                  />
                ) : (
                  <span className="text-zinc-50 font-mono font-medium">{profile.user.max_weekly_hours || 40}h</span>
                )}
              </div>

              {/* Combined table for rates and hours */}
              <div className="rounded-lg border border-zinc-800 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-zinc-800/50">
                    <tr>
                      <th className="text-left text-zinc-400 font-medium px-3 py-2 text-xs">Task Type</th>
                      <th className="text-center text-zinc-400 font-medium px-3 py-2 text-xs w-24">Rate ($)</th>
                      <th className="text-center text-zinc-400 font-medium px-3 py-2 text-xs w-24">Est. Hours</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-800">
                    {taskTypes.map(type => {
                      const rate = editingRates ? (ratesForm[type.value] || 0) : (profile.user.desired_rates?.[type.value] || 0);
                      const hours = editingRates ? (hoursForm[type.value] || 0) : (profile.user.hours_per_task_type?.[type.value] || 0);
                      return (
                        <tr key={type.value} className="hover:bg-zinc-800/30">
                          <td className="px-3 py-2 text-zinc-300">{type.label}</td>
                          <td className="px-3 py-2 text-center">
                            {editingRates ? (
                              <Input
                                type="number"
                                value={ratesForm[type.value] || ""}
                                onChange={(e) => handleRateChange(type.value, e.target.value)}
                                placeholder="0"
                                className="bg-zinc-800 border-zinc-700 text-zinc-50 w-16 h-7 text-xs px-2 mx-auto text-center"
                              />
                            ) : (
                              <span className={cn(
                                "font-mono text-xs",
                                rate > 0 ? "text-yellow-400" : "text-zinc-600"
                              )}>
                                {rate > 0 ? `$${rate}` : "—"}
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-center">
                            {editingRates ? (
                              <Input
                                type="number"
                                value={hoursForm[type.value] || ""}
                                onChange={(e) => handleHoursChange(type.value, e.target.value)}
                                placeholder="0"
                                className="bg-zinc-800 border-zinc-700 text-zinc-50 w-16 h-7 text-xs px-2 mx-auto text-center"
                              />
                            ) : (
                              <span className={cn(
                                "font-mono text-xs",
                                hours > 0 ? "text-blue-400" : "text-zinc-600"
                              )}>
                                {hours > 0 ? `${hours}h` : "—"}
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              
              {/* Warning if no rates set */}
              
              <p className="text-xs text-zinc-600 mt-3">
                Visible to project managers when assigning tasks
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Rates Required Warning - Same style as Contract Required */}
      {profile.user.role === "artist" && (!profile.user.desired_rates || Object.keys(profile.user.desired_rates).length === 0 || Object.values(profile.user.desired_rates).every(v => !v)) && (
        <Card className="bg-amber-900/20 border-amber-700 mt-6">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-400 mt-0.5 shrink-0" />
              <div>
                <h3 className="font-medium text-amber-400">Rates Required</h3>
                <p className="text-sm text-zinc-300 mt-1">
                  Please set your rates so project managers can see your expectations when assigning tasks to you.
                </p>
                <Button 
                  size="sm" 
                  onClick={() => {
                    setRatesForm(profile.user.desired_rates || {});
                    setHoursForm(profile.user.hours_per_task_type || {});
                    setMaxWeeklyHours(profile.user.max_weekly_hours || 40);
                    setEditingRates(true);
                    // Scroll to rates section
                    document.querySelector('[data-testid="rates-card"]')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="mt-3 bg-amber-600 hover:bg-amber-700 text-white"
                >
                  <CreditCard className="h-4 w-4 mr-2" />
                  Set My Rates
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Edit Contact Modal */}
      <Dialog open={editContactOpen} onOpenChange={setEditContactOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-50 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-zinc-50 flex items-center gap-2">
              <Edit2 className="h-5 w-5 text-green-400" />
              Edit Contact Info
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-zinc-400">Phone Number</Label>
              <Input
                value={contactForm.phone}
                onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                placeholder="+380 XX XXX XX XX"
                className="bg-zinc-800 border-zinc-700 text-zinc-50"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400">Telegram Username</Label>
              <Input
                value={contactForm.telegram}
                onChange={(e) => setContactForm({ ...contactForm, telegram: e.target.value })}
                placeholder="@username"
                className="bg-zinc-800 border-zinc-700 text-zinc-50"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditContactOpen(false)} className="border-zinc-700">
              Cancel
            </Button>
            <Button onClick={handleSaveContact} disabled={saving} className="bg-orange-600 hover:bg-orange-700">
              <Save className="h-4 w-4 mr-2" />
              {saving ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Profile Modal */}
      <Dialog open={editProfileOpen} onOpenChange={setEditProfileOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-50 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-zinc-50 flex items-center gap-2">
              <User className="h-5 w-5 text-sky-400" />
              Edit Profile
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-zinc-400">Display Name</Label>
              <Input
                value={profileForm.name}
                onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
                placeholder="Your name"
                className="bg-zinc-800 border-zinc-700 text-zinc-50"
                data-testid="profile-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400">Profile Photo</Label>
              <div className="flex items-center gap-3">
                {profileForm.avatar_url ? (
                  <img
                    src={profileForm.avatar_url}
                    alt="Avatar"
                    className="h-16 w-16 rounded-full object-cover border border-zinc-700"
                    onError={(e) => (e.target.style.display = "none")}
                  />
                ) : (
                  <div className="h-16 w-16 rounded-full bg-zinc-800 border border-zinc-700 flex items-center justify-center">
                    <User className="h-7 w-7 text-zinc-500" />
                  </div>
                )}
                <div>
                  <input
                    ref={avatarInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/gif"
                    className="hidden"
                    onChange={handleAvatarFile}
                    data-testid="profile-avatar-file"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => avatarInputRef.current?.click()}
                    disabled={uploadingAvatar}
                    className="border-zinc-700"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    {uploadingAvatar ? "Uploading..." : "Upload photo"}
                  </Button>
                  <p className="text-[11px] text-zinc-500 mt-1">JPEG, PNG, WebP or GIF · up to 5 MB</p>
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400">Or paste image URL</Label>
              <Input
                value={profileForm.avatar_url}
                onChange={(e) => setProfileForm({ ...profileForm, avatar_url: e.target.value })}
                placeholder="https://example.com/avatar.jpg"
                className="bg-zinc-800 border-zinc-700 text-zinc-50"
                data-testid="profile-avatar-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditProfileOpen(false)} className="border-zinc-700">
              Cancel
            </Button>
            <Button onClick={handleSaveProfile} disabled={savingProfile} className="bg-sky-600 hover:bg-sky-700">
              <Save className="h-4 w-4 mr-2" />
              {savingProfile ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Password Modal */}
      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-50 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-zinc-50 flex items-center gap-2">
              <Lock className="h-5 w-5 text-orange-400" />
              Change Password
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-zinc-400">Current Password</Label>
              <div className="relative">
                <Input
                  type={showCurrentPassword ? "text" : "password"}
                  value={passwordForm.current}
                  onChange={(e) => setPasswordForm({ ...passwordForm, current: e.target.value })}
                  placeholder="Enter current password"
                  className="bg-zinc-800 border-zinc-700 text-zinc-50 pr-10"
                  data-testid="current-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                >
                  {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className="text-zinc-400">New Password</Label>
              <div className="relative">
                <Input
                  type={showNewPassword ? "text" : "password"}
                  value={passwordForm.new}
                  onChange={(e) => setPasswordForm({ ...passwordForm, new: e.target.value })}
                  placeholder="Enter new password (min 6 characters)"
                  className="bg-zinc-800 border-zinc-700 text-zinc-50 pr-10"
                  data-testid="new-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                >
                  {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className="text-zinc-400">Confirm New Password</Label>
              <Input
                type="password"
                value={passwordForm.confirm}
                onChange={(e) => setPasswordForm({ ...passwordForm, confirm: e.target.value })}
                placeholder="Confirm new password"
                className="bg-zinc-800 border-zinc-700 text-zinc-50"
                data-testid="confirm-password-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setPasswordDialogOpen(false);
                setPasswordForm({ current: "", new: "", confirm: "" });
              }} 
              className="border-zinc-700"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleChangePassword} 
              disabled={savingPassword} 
              className="bg-orange-600 hover:bg-orange-700"
              data-testid="save-password-btn"
            >
              <Save className="h-4 w-4 mr-2" />
              {savingPassword ? "Saving..." : "Change Password"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Email Dialog */}
      <Dialog open={editEmailOpen} onOpenChange={setEditEmailOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-50 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-zinc-50 flex items-center gap-2">
              <Mail className="h-5 w-5 text-sky-400" />
              Change Email
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-zinc-400">Email Address</Label>
              <Input
                type="email"
                value={emailForm}
                onChange={(e) => setEmailForm(e.target.value)}
                placeholder="your@email.com"
                className="bg-zinc-800 border-zinc-700 text-zinc-50"
              />
              <p className="text-xs text-zinc-500">
                This email will be used for login and notifications
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditEmailOpen(false)} className="border-zinc-700">
              Cancel
            </Button>
            <Button onClick={handleSaveEmail} disabled={savingEmail} className="bg-sky-600 hover:bg-sky-700">
              <Save className="h-4 w-4 mr-2" />
              {savingEmail ? "Saving..." : "Save Email"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Payment Methods Dialog */}
      <Dialog open={editPaymentOpen} onOpenChange={setEditPaymentOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-50 max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-zinc-50 flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-purple-400" />
              Edit Payment Methods
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Preferred Method */}
            <div className="space-y-2">
              <Label className="text-zinc-400">Preferred Payment Method</Label>
              <Select 
                value={paymentForm.preferred_method} 
                onValueChange={(v) => setPaymentForm({ ...paymentForm, preferred_method: v })}
              >
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-50">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="card">Card</SelectItem>
                  <SelectItem value="fop">FOP (ФОП)</SelectItem>
                  <SelectItem value="crypto">Crypto</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Card Details */}
            <div className="p-4 bg-zinc-800/50 rounded-lg space-y-3">
              <div className="flex items-center gap-2 mb-3">
                <Badge className="bg-purple-900/30 text-purple-400 border-purple-700">Card</Badge>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs text-zinc-500">Card Number</Label>
                  <Input
                    value={paymentForm.card.card_number || ""}
                    onChange={(e) => setPaymentForm({ 
                      ...paymentForm, 
                      card: { ...paymentForm.card, card_number: e.target.value } 
                    })}
                    placeholder="4111 1111 1111 1111"
                    className="bg-zinc-900 border-zinc-700 text-zinc-50 h-9 text-sm"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-zinc-500">Bank</Label>
                  <Input
                    value={paymentForm.card.bank || ""}
                    onChange={(e) => setPaymentForm({ 
                      ...paymentForm, 
                      card: { ...paymentForm.card, bank: e.target.value } 
                    })}
                    placeholder="PrivatBank"
                    className="bg-zinc-900 border-zinc-700 text-zinc-50 h-9 text-sm"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-zinc-500">Recipient Name</Label>
                  <Input
                    value={paymentForm.card.recipient_name || ""}
                    onChange={(e) => setPaymentForm({ 
                      ...paymentForm, 
                      card: { ...paymentForm.card, recipient_name: e.target.value } 
                    })}
                    placeholder="Іванов Іван Іванович"
                    className="bg-zinc-900 border-zinc-700 text-zinc-50 h-9 text-sm"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-zinc-500">IBAN</Label>
                  <Input
                    value={paymentForm.card.recipient_iban || ""}
                    onChange={(e) => setPaymentForm({ 
                      ...paymentForm, 
                      card: { ...paymentForm.card, recipient_iban: e.target.value } 
                    })}
                    placeholder="UA..."
                    className="bg-zinc-900 border-zinc-700 text-zinc-50 h-9 text-sm"
                  />
                </div>
                <div className="space-y-1 col-span-2">
                  <Label className="text-xs text-zinc-500">IPN (ІПН)</Label>
                  <Input
                    value={paymentForm.card.recipient_ipn || ""}
                    onChange={(e) => setPaymentForm({ 
                      ...paymentForm, 
                      card: { ...paymentForm.card, recipient_ipn: e.target.value } 
                    })}
                    placeholder="1234567890"
                    className="bg-zinc-900 border-zinc-700 text-zinc-50 h-9 text-sm"
                  />
                </div>
              </div>
            </div>
            
            {/* FOP Details */}
            <div className="p-4 bg-zinc-800/50 rounded-lg space-y-3">
              <div className="flex items-center gap-2 mb-3">
                <Badge className="bg-purple-900/30 text-purple-400 border-purple-700">FOP (ФОП)</Badge>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs text-zinc-500">IBAN</Label>
                  <Input
                    value={paymentForm.fop.iban || ""}
                    onChange={(e) => setPaymentForm({ 
                      ...paymentForm, 
                      fop: { ...paymentForm.fop, iban: e.target.value } 
                    })}
                    placeholder="UA..."
                    className="bg-zinc-900 border-zinc-700 text-zinc-50 h-9 text-sm"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-zinc-500">Company Name</Label>
                  <Input
                    value={paymentForm.fop.company_name || ""}
                    onChange={(e) => setPaymentForm({ 
                      ...paymentForm, 
                      fop: { ...paymentForm.fop, company_name: e.target.value } 
                    })}
                    placeholder="ФОП Іванов І.І."
                    className="bg-zinc-900 border-zinc-700 text-zinc-50 h-9 text-sm"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-zinc-500">Bank Name</Label>
                  <Input
                    value={paymentForm.fop.bank_name || ""}
                    onChange={(e) => setPaymentForm({ 
                      ...paymentForm, 
                      fop: { ...paymentForm.fop, bank_name: e.target.value } 
                    })}
                    placeholder="АТ КБ ПриватБанк"
                    className="bg-zinc-900 border-zinc-700 text-zinc-50 h-9 text-sm"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-zinc-500">EDRPOU (ЄДРПОУ)</Label>
                  <Input
                    value={paymentForm.fop.edrpou || ""}
                    onChange={(e) => setPaymentForm({ 
                      ...paymentForm, 
                      fop: { ...paymentForm.fop, edrpou: e.target.value } 
                    })}
                    placeholder="12345678"
                    className="bg-zinc-900 border-zinc-700 text-zinc-50 h-9 text-sm"
                  />
                </div>
              </div>
            </div>
            
            {/* Crypto Details */}
            <div className="p-4 bg-zinc-800/50 rounded-lg space-y-3">
              <div className="flex items-center gap-2 mb-3">
                <Badge className="bg-purple-900/30 text-purple-400 border-purple-700">Crypto</Badge>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs text-zinc-500">Wallet Address</Label>
                  <Input
                    value={paymentForm.crypto.wallet_address || ""}
                    onChange={(e) => setPaymentForm({ 
                      ...paymentForm, 
                      crypto: { ...paymentForm.crypto, wallet_address: e.target.value } 
                    })}
                    placeholder="TRC20 or ERC20 address..."
                    className="bg-zinc-900 border-zinc-700 text-zinc-50 h-9 text-sm font-mono"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-zinc-500">Network</Label>
                  <Select 
                    value={paymentForm.crypto.network || "TRC20"} 
                    onValueChange={(v) => setPaymentForm({ 
                      ...paymentForm, 
                      crypto: { ...paymentForm.crypto, network: v } 
                    })}
                  >
                    <SelectTrigger className="bg-zinc-900 border-zinc-700 text-zinc-50 h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      <SelectItem value="TRC20">TRC20 (Tron)</SelectItem>
                      <SelectItem value="ERC20">ERC20 (Ethereum)</SelectItem>
                      <SelectItem value="BEP20">BEP20 (BSC)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditPaymentOpen(false)} className="border-zinc-700">
              Cancel
            </Button>
            <Button onClick={handleSavePaymentMethods} disabled={savingPayment} className="bg-purple-600 hover:bg-purple-700">
              <Save className="h-4 w-4 mr-2" />
              {savingPayment ? "Saving..." : "Save Payment Methods"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProfilePage;
